import { useCallback, useRef } from 'preact/hooks';

import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { usePress } from '#hooks/UNSAFE_usePress';
import { BundleType } from '#resources/nls/bundle';

import { HelpIcon } from '#PRIVATE_ThemedIcons/HelpIcon';
import { Popup } from '#UNSAFE_Popup';
import { HiddenAccessible } from '#UNSAFE_HiddenAccessible';
import { InlineHelp } from './InlineHelp';
import {
  iconUserAssistanceIcon,
  iconUserAssistancePopupFocus
} from './themes/UserAssistanceStyles.css';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { classNames } from '#utils/UNSAFE_classNames';
import { PopupRedwoodTheme } from '#UNSAFE_Popup/themes/redwood/PopupTheme';
import { useToggle } from '#hooks/UNSAFE_useToggle';

type Props = {
  /**
   * Text to provide guidance to help the user understand what data to enter.
   */
  assistiveText?: string;
  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: string;
  /**
   * Custom text to be rendered for the `helpSourceLink`. "Learn more" will be used if no custom text is provided.
   */
  helpSourceText?: string;
  /**
   * Whether the trigger element is in tab sequence
   */
  isTabbable?: boolean;
  /**
   * ID of the pop up content that can be used for aria-describedby
   */
  id?: string;
};

/**
 * Created to match icon user assistance spec for use with radio option, pending design review
 * of inline UA assistance.
 */
const IconUserAssistance = ({
  assistiveText,
  helpSourceLink,
  helpSourceText,
  isTabbable = true,
  id
}: Props) => {
  const { bool: isOpen, setFalse: setIsOpenFalse, toggle } = useToggle(false);

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const userAssistance_learnMoreStr = translations.userAssistance_learnMore();
  const sourceText = helpSourceLink && (helpSourceText || userAssistance_learnMoreStr);
  const helpForRadioString = translations.radio_helpForRadio();
  const helpStringId = `${id}-help-string`;
  const anchorRef = useRef<HTMLSpanElement>(null);

  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      const target = event?.target;
      // We need to filter out anchor click events, because anchor is responsible for the toggling
      if (
        (target instanceof Element || target instanceof Document) &&
        !anchorRef.current?.contains(target) &&
        isOpen
      ) {
        setIsOpenFalse();
      }
    },
    [isOpen, setIsOpenFalse]
  );

  const handleOnKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // allow user to close popup without having to focus it
      if (['Tab', 'Escape'].includes(e.key) && isOpen) {
        setIsOpenFalse();
      }

      // prevent scroll jump
      if (e.code === 'Space') {
        e.preventDefault();
      }
    },
    [isOpen, setIsOpenFalse]
  );

  const { pressProps } = usePress((e) => {
    e.preventDefault();
    toggle();
  });

  const handleLinkEvent = useCallback(
    (e: Event) => {
      // if it's a key event that is not "enter", return early
      const { key } = e as KeyboardEvent;
      if (key && key !== 'Enter') {
        return;
      }

      // We want to close the pop up if a link is triggered with mouse or keyboard
      const { tagName } = e.target as Element;

      if (tagName?.toLowerCase() === 'a') {
        setIsOpenFalse();

        // we want to refocus to the help icon after the browser has launched the link
        // prematurely doing so prevents keyboard events from firing
        setTimeout(() => anchorRef.current?.focus());
      }
    },
    [setIsOpenFalse]
  );

  // We are referencing popup vars, so we need to bring in the baseTheme for popup
  const { baseTheme: popupBaseTheme } = useComponentTheme(PopupRedwoodTheme);
  const iconUserAssistanceClasses = classNames([popupBaseTheme, iconUserAssistancePopupFocus]);

  return helpSourceLink || assistiveText ? (
    <>
      <span
        {...pressProps}
        role="link"
        aria-describedby={helpStringId}
        aria-haspopup="dialog"
        ref={anchorRef}
        tabIndex={isTabbable ? 0 : -1}
        class={iconUserAssistanceIcon}
        onKeyDown={handleOnKeyDown}
        id={`${id}-help-icon`}>
        <HelpIcon />
      </span>
      <HiddenAccessible id={helpStringId} isHidden={true}>
        {helpForRadioString}
      </HiddenAccessible>
      <Popup
        placement="top"
        isOpen={isOpen}
        anchorRef={anchorRef}
        onClose={setIsOpenFalse}
        onClickOutside={handleOutsideClick}
        shiftOptions={{ mainAxis: true, crossAxis: false }}
        maxWidth={'296px'}>
        <div
          id={id}
          tabIndex={0}
          class={iconUserAssistanceClasses}
          onMouseUp={handleLinkEvent}
          onKeyDown={handleLinkEvent}>
          <InlineHelp
            assistiveText={assistiveText}
            sourceLink={helpSourceLink}
            sourceText={sourceText}
          />
        </div>
      </Popup>
    </>
  ) : null;
};

export { IconUserAssistance };
